import {
  ProjectCreateRequest,
  ProjectDuplicateRequest,
  ProjectEditRequest,
} from '~/common/entities/Project/ProjectRequest';
import {
  CreateJobValues,
  getProjectsWithUpdatedStepNumbersInProjectOfCreatedJob,
} from '../utils/projectUtils';
import {
  ProjectDuplicateResponse,
  ProjectResponse,
} from '~/common/entities/Project/ProjectResponse';
import { GetProjectResponse } from '~/common/entities/Project/GetProjectResponse';
import { CreateJobResponse } from '~/common/entities/Project/CreateJobResponse';
import { CreateJobRequest } from '~/common/entities/Project/CreateJobRequest';
import { EditJobResponse } from '~/common/entities/Project/EditJobResponse';
import { MergeJobRequest } from '~/common/entities/Project/MergeJobRequest';
import { EditJobRequest } from '~/common/entities/Project/EditJobRequest';
import { Job, Project } from '~/common/entities/Project/ProjectTypes';
import { useAuthenticationService } from './AuthenticationService';
import { ApiErrorCallback, ApiSuccessCallback } from './callback';
import { authenticatedApi } from '../utils/authenticatedApi';
import { Nothing } from '~/common/entities/store/types';
import { createContainer } from 'unstated-next';
import { useState } from 'react';
import _ from 'lodash';

function useProject() {
  const [projects, setProjects] = useState<Project[]>();
  const [openProject, setOpenProjectState] = useState<Project | undefined>();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const { authenticationState } = useAuthenticationService();

  const setOpenProject = (project: Project) => {
    if (!project) setOpenProjectState(undefined);
    if (project?.jobs) {
      setOpenProjectState(project);
    } else {
      getProjectFromServer(project, setOpenProjectState);
    }
  };

  const setProjectsOrdered = (newProjects: Project[]) => {
    setProjects(
      _.sortBy(newProjects, [(project) => project.name.toLowerCase()]),
    );
  };

  const addProject = async (
    name: string,
    subProjects: string[],
    calculated_project_time: number,
    is_option: boolean,
    onSuccess?: ApiSuccessCallback<ProjectResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const values: ProjectCreateRequest = {
      name,
      sub_projects: subProjects,
      calculated_project_time,
      is_option,
    };

    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).post<ProjectResponse, ProjectCreateRequest>('/project/', values);
    if (ApiError && onError) onError(ApiError);
    if (data) {
      setProjectsOrdered([...projects, data]);
      setOpenProject(data);
    }
    if (data && onSuccess) {
      onSuccess(data);
    }
  };

  const editProject = async (
    project: Project,
    name: string,
    subProjects: string[],
    calculated_project_time: number,
    is_option: boolean,
    onSuccess?: ApiSuccessCallback<ProjectResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const values: ProjectEditRequest = {
      name,
      sub_projects: subProjects,
      calculated_project_time,
      is_option,
    };
    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).put<ProjectResponse, ProjectEditRequest>(
      `/project/${project.id}/`,
      values,
    );

    if (ApiError && onError) onError();
    if (data) {
      const tmpProjects = projects
        .filter((tmpProject) => tmpProject.id !== project.id)
        .map((tmpProject) => {
          const sp = data.options?.find((p) => p.id == tmpProject.id);
          if (sp) return { ...tmpProject, is_option: true };
          return tmpProject;
        });
      setProjectsOrdered([...tmpProjects, data]);
    }
    if (data && onSuccess) {
      onSuccess(data);
    }
  };

  const deleteProject = async (
    project: Project,
    onError?: ApiErrorCallback,
  ) => {
    const { error } = await authenticatedApi(
      authenticationState,
    ).delete<Nothing>(`/project/${project.id}/`);
    if (error && onError) onError();
    if (!error) {
      setProjectsOrdered(projects.filter((p) => p.id !== project.id));
    }
  };

  const duplicateProject = async (
    project: Project,
    newName: string,
    onSuccess?: ApiSuccessCallback<ProjectResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const values: ProjectDuplicateRequest = {
      new_name: newName,
    };

    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).post<ProjectDuplicateResponse, ProjectDuplicateRequest>(
      `/project/${project.id}/duplicate/`,
      values,
    );
    if (ApiError && onError) onError(ApiError);
    if (data) {
      setOpenProject(data);
      data.openEditProject = true;
      setProjectsOrdered([
        ...projects.filter((p) => p.id != project.id),
        project,
        data,
      ]);
    }
    if (data && onSuccess) onSuccess(data);
  };

  const createJob = async (
    values: CreateJobValues,
    onSuccess?: ApiSuccessCallback<CreateJobResponse>,
    onError?: ApiErrorCallback,
  ) => {
    //stepNumbers of the other jobs have to be updated
    const projectsAndStepNumberOfCreatedJob =
      getProjectsWithUpdatedStepNumbersInProjectOfCreatedJob(projects, values);
    const tmpProjects = projectsAndStepNumberOfCreatedJob.projects;
    const { stepNumberOfCreatedJob } = projectsAndStepNumberOfCreatedJob;

    const { data, error } = await authenticatedApi(authenticationState).post<
      CreateJobResponse,
      CreateJobRequest
    >('/job/', {
      project: values.project,
      name: values.name,
      duration:
        values.durationHours * 60 * 60 +
        values.durationMinutes * 60 +
        values.durationSeconds,
      step_number: stepNumberOfCreatedJob,
      description: values.description,
      done_per_project: values.done_per_project,
      department: values.departmentId !== '' ? values.departmentId : undefined,
    });

    if (error && onError) return onError(error);
    setProjectsOrdered(tmpProjects);
    if (onSuccess) return onSuccess(data);
  };

  const editJob = async (
    editJobRequest: EditJobRequest,
    jobId: string,
    onSuccess?: ApiSuccessCallback<EditJobResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).put<EditJobResponse, EditJobRequest>(`/job/${jobId}/`, editJobRequest);

    if (ApiError && onError) onError();
    if (data && onSuccess) onSuccess(data);
  };
  const updateProjectLocally = (
    project: Project,
    onSuccess?: ApiSuccessCallback<Project>,
  ) => {
    setProjectsOrdered([
      ...projects.filter((p) => p.id != project.id),
      project,
    ]);
    if (onSuccess) onSuccess(project);
  };

  const getAllJobsOfProject = async (project: Project | string | number) => {
    const id =
      typeof project === 'string' || typeof project === 'number'
        ? project
        : project.id;
    const { data } = await authenticatedApi(authenticationState).get<Job[]>(
      `jobs/${id}/`,
    );
    const nProject = projects?.find((p) => p.id == id);
    if (nProject) {
      nProject.jobs = data;
      updateProjectLocally(nProject);
    }
  };

  const deleteJob = async (
    job: Job,
    project: Project,
    onSuccess?: () => void,
    onError?: ApiErrorCallback,
  ) => {
    const { error } = await authenticatedApi(
      authenticationState,
    ).delete<Nothing>(`/job/${job.id}/`);
    if (error && onError) onError();
    if (!error) {
      await getAllJobsOfProject(project);
      if (onSuccess) onSuccess();
    }
  };

  const getProjectFromServer = async (
    project: Project,
    onSuccess?: ApiSuccessCallback<Project>,
    onError?: ApiErrorCallback,
  ) => {
    if (!project) {
      return onError ? onError(undefined) : undefined;
    }
    const { data, error } = await authenticatedApi(
      authenticationState,
    ).get<Project>(`/project/${project.id}/`);
    if (error) {
      return onError ? onError(error) : undefined;
    }
    if (data) {
      if (projects) {
        const tmpProjects = [...projects.filter((p) => p.id !== project.id)];
        tmpProjects.push(data);
        setProjectsOrdered(tmpProjects);
      }
    }
    if (onSuccess) onSuccess(data);
  };

  const getJobFromServer = async (
    jobId: number,
    onSuccess?: ApiSuccessCallback<Job>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } = await authenticatedApi(
      authenticationState,
    ).get<Job>(`/job/${jobId}/`);
    if (error) {
      return onError ? onError(error) : undefined;
    }
    if (onSuccess) {
      onSuccess(data);
    }
    return data;
  };

  const fillStoreWithServerData = async (
    onSuccess?: ApiSuccessCallback<Project[]>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } =
      await authenticatedApi(authenticationState).get<GetProjectResponse>(
        '/projects/',
      );

    if (error) {
      return onError ? onError(error) : undefined;
    }
    if (data.length > 0) {
      setProjectsOrdered(data);
    }
    return onSuccess ? onSuccess(data) : undefined;
  };

  const mergeJobs = async (
    jobIds: string[],
    project: Project,
    onSuccess?: ApiSuccessCallback<Job>,
    onError?: ApiErrorCallback,
  ) => {
    const firstJobId = jobIds[0];
    const otherJobIds = jobIds.filter((jobId) => jobId !== firstJobId);

    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).patch<Job, MergeJobRequest>(`/job/merge/${firstJobId}/`, {
      jobs: otherJobIds,
    });

    if (ApiError) {
      if (onError) onError();
    } else {
      await getProjectFromServer(project);
      if (onSuccess) onSuccess(data);
    }
  };

  return {
    projects,
    tabIndex,
    setTabIndex,
    openProject,
    setOpenProject,
    setProjectsOrdered,
    addProject,
    editProject,
    deleteProject,
    duplicateProject,
    createJob,
    editJob,
    deleteJob,
    mergeJobs,
    getProjectFromServer,
    getJobFromServer,
    updateProjectLocally,
    fillStoreWithServerData,
  };
}

export const { Provider: ProjectProvider, useContainer: useProjectsService } =
  createContainer(useProject);
